<template>
  <teleport to="body">
    <v-navigation-drawer v-model="drawerOpen" location="right" temporary width="520">
      <div class="ncrm-privacy-purpose-drawer pa-5 d-flex flex-column ga-4">
        <div class="d-flex align-start justify-space-between ga-3">
          <div>
            <div class="text-caption text-medium-emphasis">{{ purpose ? purpose.code : $t(`${copyKey}.newEyebrow`) }}</div>
            <h3 class="text-h6">{{ purpose ? purpose.name : $t(`${copyKey}.newTitle`) }}</h3>
            <v-chip v-if="purpose" :color="statusColor" class="mt-1">
              {{ $t(`${copyKey}.status.${purpose.status}`) }}
            </v-chip>
          </div>
          <v-btn icon="mdi-close" variant="text" :aria-label="$t('app.actions.close')" @click="drawerOpen = false" />
        </div>

        <v-alert v-if="!canManage" type="info" density="compact" icon="mdi-lock-outline">
          {{ $t(`${copyKey}.readOnly`) }}
        </v-alert>
        <v-alert v-else-if="purpose?.status === 'retired'" type="info" density="compact">
          {{ $t(`${copyKey}.retiredHint`) }}
        </v-alert>
        <v-alert v-else-if="purpose?.status === 'active'" type="info" density="compact">
          {{ $t(`${copyKey}.activeHint`) }}
        </v-alert>
        <v-alert v-if="errorMessage" type="error" density="compact">{{ errorMessage }}</v-alert>

        <v-text-field v-if="!purpose" v-model="form.code" :label="$t(`${copyKey}.fields.code`)"
          :hint="$t(`${copyKey}.codeHint`)" persistent-hint :rules="[codeRule]" maxlength="64" />
        <v-text-field v-model="form.name" :label="$t(`${copyKey}.fields.name`)"
          :disabled="!can('name')" maxlength="191" />
        <v-textarea v-model="form.description" :label="$t(`${copyKey}.fields.description`)"
          :disabled="!can('description')" rows="2" auto-grow maxlength="500" />
        <v-select v-model="form.category" :items="options('categories', categories)" :label="$t(`${copyKey}.fields.category`)"
          :disabled="!can('category')" />
        <v-select v-model="form.channels" :items="options('channels', channels)" :label="$t(`${copyKey}.fields.channels`)"
          multiple chips :disabled="!can('channels')" />
        <v-select v-model="form.default_lawful_basis_code" :items="basisItems" :label="$t(`${copyKey}.fields.basis`)"
          :disabled="!can('default_lawful_basis_code')" />

        <div>
          <v-select v-model="form.unknown_policy" :items="policyItems" :label="$t(`${copyKey}.fields.unknownPolicy`)"
            hide-details :disabled="!can('unknown_policy')" />
          <div class="d-flex align-center flex-wrap ga-2 mt-2 text-body-2">
            <span>{{ $t(`${copyKey}.effective`, { policy: policyLabel(effective.value) }) }}</span>
            <v-chip size="x-small">{{ $t(`${copyKey}.source.${effective.source}`) }}</v-chip>
            <v-btn v-if="form.unknown_policy && can('unknown_policy')" size="small" variant="text" @click="form.unknown_policy = null">
              {{ $t(`${copyKey}.useCompanyPolicy`) }}
            </v-btn>
          </div>
        </div>

        <v-switch v-model="form.double_opt_in_enabled" color="primary" inset :disabled="!can('double_opt_in_enabled')"
          :label="$t(`${copyKey}.fields.doubleOptIn`)" :hint="$t(`${copyKey}.doubleOptInHint`)" persistent-hint />
        <v-switch v-model="form.show_in_contacts_summary" color="primary" inset :disabled="!can('show_in_contacts_summary')"
          :label="$t(`${copyKey}.fields.inSummary`)" hide-details />

        <div v-if="canManage && purpose?.status !== 'retired'" class="d-flex flex-wrap ga-2">
          <v-btn color="primary" variant="flat" :loading="busy === 'save'" :disabled="!canSave || Boolean(busy)" @click="save">
            {{ $t(`${copyKey}.${purpose ? "save" : "create"}`) }}
          </v-btn>
          <v-btn v-if="purpose?.status === 'draft'" variant="tonal" color="primary" :disabled="Boolean(busy)" @click="confirm = 'activate'">
            {{ $t(`${copyKey}.activate`) }}
          </v-btn>
          <v-btn v-if="purpose" variant="text" color="error" :disabled="Boolean(busy)" @click="openRetire">
            {{ $t(`${copyKey}.retire`) }}
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <v-dialog :model-value="Boolean(confirm)" max-width="520" persistent>
      <v-card v-if="confirm">
        <v-card-title>{{ $t(`${copyKey}.confirm.${confirm}.title`, { name: purpose?.name }) }}</v-card-title>
        <v-card-text>
          <p class="mb-2">{{ $t(`${copyKey}.confirm.${confirm}.body`) }}</p>
          <p v-if="confirm === 'retire'" class="mb-0 font-weight-medium">
            {{ impact === null ? $t(`${copyKey}.confirm.retire.impactUnknown`) : $t(`${copyKey}.confirm.retire.impact`, { count: impact }) }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="Boolean(busy)" @click="confirm = null">{{ $t(`${copyKey}.cancel`) }}</v-btn>
          <v-btn :color="confirm === 'retire' ? 'error' : 'primary'" variant="flat" :loading="Boolean(busy)" @click="runConfirmed">
            {{ $t(`${copyKey}.confirm.${confirm}.action`) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </teleport>
</template>

<script>
import {
  activatePurpose, countPurposeContactPoints, createPurpose, retirePurpose, updatePurpose,
} from "./privacyPurposeCatalogApi.mjs";
import {
  LAWFUL_BASES, PURPOSE_CATEGORIES, PURPOSE_CHANNELS, PURPOSE_CODE_PATTERN, UNKNOWN_POLICIES,
  createPayload, editableFields, effectivePolicy, emptyPurposeForm, purposeChanges, purposeForm,
} from "./privacyPurposeModel.mjs";

const ERROR_KEYS = Object.freeze({
  privacy_purpose_code_taken: "codeTaken",
  privacy_purpose_conflict: "conflict",
  privacy_purpose_locked: "locked",
  privacy_purpose_retired: "retired",
  privacy_not_found: "notFound",
});

export default {
  name: "NcrmPrivacyPurposeDrawer",
  props: {
    modelValue: { type: Boolean, default: false },
    // `null` deschide formularul de creare.
    purpose: { type: Object, default: null },
    settings: { type: Object, default: null },
    canManage: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "saved"],
  data: () => ({ form: emptyPurposeForm(), busy: null, errorMessage: "", confirm: null, impact: null }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.workspace.purposes.drawer"; },
    drawerOpen: {
      get() { return this.modelValue; },
      set(value) { this.$emit("update:modelValue", value); },
    },
    categories() { return PURPOSE_CATEGORIES; },
    statusColor() { return { active: "success", draft: "info", retired: undefined }[this.purpose?.status]; },
    channels() { return PURPOSE_CHANNELS; },
    editable() { return this.canManage ? editableFields(this.purpose?.status || "draft") : []; },
    effective() { return effectivePolicy({ unknown_policy: this.form.unknown_policy }, this.settings); },
    basisItems() {
      return [{ value: null, title: this.$t(`${this.copyKey}.noBasis`) },
        ...LAWFUL_BASES.map((value) => ({ value, title: this.$t(`ncrm.contacts.dataProtection.record.bases.${value}`) }))];
    },
    policyItems() {
      return [
        { value: null, title: this.$t(`${this.copyKey}.companyPolicy`, { policy: this.policyLabel(this.settings?.unknown_policy) }) },
        ...UNKNOWN_POLICIES.map((value) => ({ value, title: this.policyLabel(value) })),
      ];
    },
    changes() { return this.purpose ? purposeChanges(this.purpose, this.form) : null; },
    canSave() {
      if (this.purpose) return Object.keys(this.changes).length > 0 && Boolean(this.form.name.trim()) && this.form.channels.length > 0;
      return PURPOSE_CODE_PATTERN.test(this.form.code.trim()) && Boolean(this.form.name.trim()) && this.form.channels.length > 0;
    },
  },
  watch: {
    modelValue(open) { if (open) this.reset(); },
  },
  methods: {
    reset() {
      this.form = this.purpose ? purposeForm(this.purpose) : emptyPurposeForm();
      this.busy = null; this.errorMessage = ""; this.confirm = null; this.impact = null;
    },
    can(field) { return this.editable.includes(field); },
    codeRule(value) { return PURPOSE_CODE_PATTERN.test((value || "").trim()) || this.$t(`${this.copyKey}.codeHint`); },
    // Etichetele refolosesc vocabularul tabului de record.
    options(group, values) { return values.map((value) => ({ value, title: this.$t(`ncrm.contacts.dataProtection.record.${group}.${value}`) })); },
    policyLabel(value) { return value ? this.$t(`ncrm.contacts.dataProtection.workspace.purposes.policies.${value}`) : "—"; },
    // Succesul închide drawer-ul și lasă părintele să reîncarce; eroarea rămâne inline.
    async run(kind, request) {
      this.busy = kind; this.errorMessage = "";
      try {
        await request();
        this.confirm = null;
        this.$emit("saved", kind);
        this.drawerOpen = false;
      } catch (error) {
        const key = ERROR_KEYS[error?.response?.data?.code] || "generic";
        this.errorMessage = this.$t(`${this.copyKey}.errors.${key}`);
        this.confirm = null;
      } finally {
        this.busy = null;
      }
    },
    save() {
      return this.run("save", () => (this.purpose
        ? updatePurpose(this.purpose.id, this.changes, this.purpose.version)
        : createPurpose(createPayload(this.form))));
    },
    async openRetire() {
      this.impact = null;
      this.confirm = "retire";
      try { this.impact = await countPurposeContactPoints(this.purpose.id); } catch { this.impact = null; }
    },
    runConfirmed() {
      const id = this.purpose.id;
      return this.confirm === "retire"
        ? this.run("retire", () => retirePurpose(id))
        : this.run("activate", () => activatePurpose(id));
    },
  },
};
</script>

<style scoped>
.ncrm-privacy-purpose-drawer { text-align: left; }
</style>
